import React, {
  useCallback,
  useContext,
  createContext,
  useState,
  useEffect,
  useRef,
} from "react";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { message, Modal } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import GetQueryParamErrors from "../QueryParamsErrors/GetQueryParamErrors";
import { API_BASE_URL, DEBUG_ACTIVE } from "../../api";
import { setAuthHint } from "../utils/authHint";

// The API runs on Cloud Run, which can take 15-30s to cold-start an
// instance. A single 9s timeout meant the very first request of a session
// (exactly the one a brand-new account makes) errored out and bounced the
// user to /login. Instead, retry with escalating per-attempt timeouts so a
// cold start reads as a slightly longer spinner rather than a failure.
const CHECK_AUTH_TIMEOUTS_MS = [9000, 20000, 35000];
const CHECK_AUTH_RETRY_DELAY_MS = 1000;

// An error with no HTTP response (timeout, abort, network drop) or a
// gateway error emitted while Cloud Run is still booting the instance is
// worth retrying; a real application response (401, 500, ...) is not.
const isColdStartError = (error) =>
  !error.response || [502, 503, 504].includes(error.response.status);

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  // USE STATES
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({});

  // FUNCTIONS
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserInformation, setCurrentUserInformation] = useState({});
  const [uploadingFile, setUploadingFile] = useState({
    thumbnail: null,
    uploadStatus: "none",
  });
  // Start in "pending" so route guards show their loader on first paint while
  // the initial checkAuth() runs. Starting in "success" made protected routes
  // briefly render with a null user before the auth check resolved, causing a
  // flash of broken/logged-out content on refresh.
  const [authState, setAuthState] = useState({
    status: "pending",
    error: null,
  });
  // Refs
  const [searchParams] = useSearchParams();

  const getRefferal = searchParams.get("ref");

  // Axios configuration for requests
  const axiosInstance = axios.create({
    withCredentials: true,
    baseURL: `${API_BASE_URL}`,
  });
  // Utils functions:
  const applyReferral = async () => {
    const res = await axios.get(`${API_BASE_URL}/affiliate/confirm`, {
      params: { code: getRefferal }, // cleaner than string-concat
      withCredentials: true,
    });

    return res;
  };
  function clearAuthCookies() {
    document.cookie.split(";").forEach(function (c) {
      // Clear each cookie by setting its expiration to a past time
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/");
    });
  }

  function clearLocalStorage() {
    try {
      localStorage.clear();
    } catch (e) {
      if (DEBUG_ACTIVE) {
        console.error("Error clearing local storage:", e);
      }
    }
  }

  // Cached promise to prevent duplicate authentication checks
  let authPromiseRef = useRef(null);

  // Function to check if the user is logged in
  // Fast, deduped auth check with AbortController and minimal UI jank.
  const checkAuth = useCallback(async () => {
    if (authPromiseRef.current) return authPromiseRef.current;

    authPromiseRef.current = (async () => {
      // Track mount state to avoid setState on unmounted components
      let isActive = true;
      const cleanup = () => {
        isActive = false;
        authPromiseRef.current = null;
      };

      try {
        for (
          let attempt = 0;
          attempt < CHECK_AUTH_TIMEOUTS_MS.length;
          attempt++
        ) {
          const attemptTimeout = CHECK_AUTH_TIMEOUTS_MS[attempt];
          const isLastAttempt = attempt === CHECK_AUTH_TIMEOUTS_MS.length - 1;
          const controller = new AbortController();
          // Abort slightly after axios' own timeout so axios reports the
          // clearer ECONNABORTED instead of a cancellation.
          const timeoutId = setTimeout(
            () => controller.abort(),
            attemptTimeout + 1000
          );

          try {
            const response = await axios.get(
              `${API_BASE_URL}/users/api/checkAuth/`,
              {
                withCredentials: true,
                signal: controller.signal,
                timeout: attemptTimeout,
              }
            );

            if (!isActive) return false;

            setCurrentUser(response.data);
            setCurrentUserInformation({
              plan: response.data.Plan,
              isBanned: response.data.PlanStatus === "Banned",
            });
            setAuthState({ status: "success" });
            // Remember the signed-in state so route guards skip the blocking
            // spinner on the next visit (see utils/authHint.js).
            setAuthHint(true);
            // Set Sentry user context for better error tracking
            Sentry.setUser({
              id: response.data._id,
              email: response.data.email,
              plan: response.data.Plan,
            });
            return true;
          } catch (error) {
            if (!isActive) return false;

            if (error.response?.status === 401) {
              // Use the same "No user" status the route guards check for, so an
              // unauthenticated visitor is redirected cleanly instead of briefly
              // slipping past the guard on a mismatched status string.
              setAuthState({ status: "No user" });
              setCurrentUser(null);
              setCurrentUserInformation({});
              setAuthHint(false);
              Sentry.setUser(null);
              return false;
            }

            if (isColdStartError(error) && !isLastAttempt) {
              // Keep the status "pending" so route guards keep showing their
              // spinner instead of redirecting mid-retry.
              await new Promise((resolve) =>
                setTimeout(resolve, CHECK_AUTH_RETRY_DELAY_MS * (attempt + 1))
              );
              continue;
            }

            // Normalize error messages for UI consistency
            let uiMessage = "Authentication failed";
            if (axios.isCancel(error))
              uiMessage = "Authentication request canceled";
            else if (error.code === "ECONNABORTED")
              uiMessage = "Request timed out";
            else if (error.response?.data?.message)
              uiMessage = error.response.data.message;

            setAuthState({ status: "error", error: uiMessage });
            message.error(uiMessage);
            setCurrentUser(null);
            setCurrentUserInformation({});
            // Track auth errors in Sentry (excluding 401 which is expected)
            Sentry.captureException(error, {
              tags: { auth_error: true, error_type: "checkAuth" },
              extra: { uiMessage, attempts: attempt + 1 },
            });
            return false;
          } finally {
            clearTimeout(timeoutId);
          }
        }

        return false;
      } catch (error) {
        if (!isActive) return false;

        setAuthState({
          status: "error",
          error: error?.message || "Authentication failed",
        });
        setCurrentUser(null);
        setCurrentUserInformation({});
        Sentry.captureException(error, {
          tags: { auth_error: true, error_type: "checkAuth" },
        });
        return false;
      } finally {
        cleanup();
      }
    })();

    return authPromiseRef.current;
  }, []);

  useEffect(() => {
    let isMounted = true; // Track if the component is still mounted

    checkAuth().catch((error) => {
      if (isMounted) {
        setAuthState({ status: "error", error: error.message });
      }
    });

    return () => {
      isMounted = false; // Cleanup function to prevent state updates on unmounted component
    };
  }, [checkAuth]);

  // Function to log in the user
  async function login(payload) {
    setAuthState({ status: "pending" });
    const config = {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
      // Generous timeout: the first request of a session may have to wait out
      // a Cloud Run cold start (15-30s) before the API can even respond.
      timeout: 30000,
    };
    try {
      const response = await axios.post(
        `${API_BASE_URL}/users/api/login/`,
        payload,
        config
      );
      if (response.status === 200) {
        message.success("Logged in successfully");
        setCurrentUser(response.data.data);
        setAuthHint(true);

        setAuthState({ status: "success" });
        navigate("/dashboard?login=true");
      } else {
        message.error("Login failed");
        setAuthState({ status: "error" });
      }
    } catch (error) {
      if (DEBUG_ACTIVE) {
        console.error("Login error:", error);
      }
      Sentry.captureException(error, {
        tags: { auth_error: true, error_type: "login" },
        extra: { email: payload?.email },
      });
      setAuthState({ status: "error" });

      const serverMessage = error?.response?.data?.message;

      // A not-yet-activated account can never log in to reach the in-app
      // "resend verification" page, so offer to resend the activation link
      // right here using the email they just tried.
      if (
        error?.response?.status === 400 &&
        /not activated/i.test(serverMessage || "")
      ) {
        Modal.confirm({
          title: "Verify your email to continue",
          content: `Your account isn't activated yet. We can email a new verification link to ${payload?.email}.`,
          okText: "Resend verification email",
          cancelText: "Close",
          onOk: () => resendVerification(payload?.email),
        });
        return;
      }

      setSnackbar({
        open: true,
        message: serverMessage || "Something went wrong. Please try again.",
        severity: "error",
      });
    }
  }

  // Resend the account-activation email. Works without a session because the
  // user typically cannot log in yet (their account is not activated). The
  // backend responds generically regardless of whether the email exists, so we
  // always show the same neutral confirmation.
  async function resendVerification(email) {
    const neutralMessage =
      "If that email needs verification, a new link is on its way. Please check your inbox and spam folder.";
    if (!email) {
      message.info(neutralMessage);
      return;
    }
    try {
      await axios.post(
        `${API_BASE_URL}/users/api/resend-verification-email`,
        { email },
        { timeout: 10000 }
      );
    } catch (error) {
      if (DEBUG_ACTIVE) {
        console.error("Resend verification error:", error);
      }
      // Fall through to the same neutral message: never reveal account state,
      // and don't alarm the user if the request hiccups.
    } finally {
      message.success(neutralMessage);
    }
  }

  // Function to log out the user
  function logout() {
    setAuthState({ status: "pending" });
    const config = {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    };
    axiosInstance
      .post(`${API_BASE_URL}/users/api/logout/`, {}, config)
      .then((res) => {
        clearAuthCookies();
        clearLocalStorage();
        // clearLocalStorage() already wiped the hint; this keeps the
        // signed-out marker correct even if that clear ever throws.
        setAuthHint(false);

        setCurrentUser(null);
        setCurrentUserInformation({});
        Sentry.setUser(null);
        setAuthState({ status: "success", message: res.data.message });
        message.success("Logged out successfully");
        navigate("/?logout=true");
      })
      .catch((error) => {
        if (DEBUG_ACTIVE) {
          console.error("Logout error:", error);
        }
        Sentry.captureException(error, {
          tags: { auth_error: true, error_type: "logout" },
        });
        setSnackbar({
          open: true,
          message:
            error?.response?.data?.message ||
            "Something went wrong. Please try again.",
          severity: "error",
        });
        setAuthState({ status: "error" });
      });
  }

  // Function to register the user
  async function register(payload) {
    setAuthState({ status: "pending" });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/users/api/register/`,
        payload,
        {
          timeout: 30000,
          useCredentials: true,
        }
      );
      if (response?.status !== 200 && response?.status !== 201) {
        throw new Error("Register failure.");
      }
      setCurrentUser(response.data.data);
      // Refferal logic
      if (getRefferal) {
        const { data: userData } = await axios.get(
          `${API_BASE_URL}/users/api/id`,
          {
            params: { email: response?.data?.email },
            withCredentials: true,
            timeout: 300000,
          }
        );
        const { status, data: refData } = await applyReferral(userData.userId);
        if (status === 200) {
          message.success(refData.message);
        } else {
          message.error("Something went wrong while applying the referral.");
        }
      } // End of Refferal Logic
      setAuthHint(true);
      setAuthState({ status: "success" });
    } catch (error) {
      if (DEBUG_ACTIVE) {
        console.error("Register error:", error);
      }
      Sentry.captureException(error, {
        tags: { auth_error: true, error_type: "register" },
        extra: { email: payload?.email },
      });
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Something went wrong. Please try again.",
        severity: "error",
      });
      setAuthState({ status: "error" });
    }
  }

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (tokenResponse) => {
      setAuthState({ status: "pending" });

      // Fetching user info can be done on the client or the server
      await axios.catch(() => {
        message.error("There was an issue signing in");
        setAuthState({ status: "error" });
      });
    },
  });

  // Fetch a user's post
  async function fetchPost(postId) {
    setAuthState({ status: "pending" });
    try {
      const response = await axios.post(
        `${API_BASE_URL}/videos/api/get_video`,
        { video_id: postId },
        {
          withCredentials: true,
          timeout: 50000,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      setAuthState({ status: "success" });

      return response.data;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { error_type: "fetchPost" },
        extra: { postId },
      });
      message.error("Error fetching post: " + error.message);
      setAuthState({ status: "error" });
      throw new Error("Error fetching post: " + error.message);
    }
  }

  const messageSent = useRef(false);
  useEffect(() => {
    if (snackbar.open && !messageSent.current) {
      messageSent.current = true;
      if (authState.status === "error") {
        message.error(snackbar.message);
      } else {
        message.info(snackbar.message);
      }
    }
  }, [snackbar.open, snackbar.message, authState.status]);
  const authReady = authState.status !== "pending";

  const value = {
    currentUser,
    authReady,
    logout,
    login,
    register,
    resendVerification,
    currentUserInformation,
    uploadingFile,
    setUploadingFile,
    authState,
    fetchPost,
    googleLogin,
  };

  return (
    <AuthContext.Provider value={value}>
      {GetQueryParamErrors()}
      {children}
    </AuthContext.Provider>
  );
}